import { PickSnippetInput, SportslineWebComponent_PickSnippetParlayQuery } from '@lib/graphql-types';
import { Component, Host, h, Element, State, Prop } from '@stencil/core';
// import mockedData from './test/__mocks__/response';
import { generateRandomAlphaNumericId, isUndefinedOrNull } from '@utils/commonUtils';
import { fromPickSnippetToPickSnippetCardProps, getDataFromAttribute } from '@utils/sidelineUtils';
import { sportslineGraphQLClient } from '@lib/graphql-client';
import { observeVisibilityAndTrigger } from '@utils/html';
import { isGameAvailableForBetting } from '@utils/bettingUtils';
import { fromPickSnippetAttributeToTrackingImpressionWidgetDataBets, fromPickSnippetAttributeToTrackingViewedWidgetDataBets, toPickSnippetInput } from '@utils/pickSnippet';
import { eventsName, PickSnippetEventViewed, setInstanceIdAttribute, trackEvent } from '@utils/trackingUtils';
import { getBookAssetsById, getBookSloganById } from '@utils/books';

@Component({
  tag: 'sportsline-pick-snippet-parlay',
  styleUrl: 'sportsline-pick-snippet-parlay.css',
  shadow: true,
})
export class SportslinePickSnippetParlay {
  /**
   * Whether the snippet is branded
   */
  @Prop() isBranded: boolean = false;
  /**
   * Book ID, e.g., "35"
   */
  @Prop() book: string | null = null;
  /**
   * Book Name, e.g., "FanDuel"
   */
  @Prop() bookName: string | null = null;
  /**
   * Vendor Book ID, e.g., "value"
   */
  @Prop() vendorBookId: string | null = null;

  @State() data: SportslineWebComponent_PickSnippetParlayQuery['pickSnippetParlay'] = null;
  @State() loading: boolean = true;
  @State() error: string | null = null;
  @State() initialized: boolean = false;

  @Element() el: HTMLSportslinePickSnippetParlayElement;

  private instanceId: string = generateRandomAlphaNumericId();
  private version: string = '1.0.0';
  private get name() {
    // Updating name based on branding
    if (this.getIsBrandedDataValid) {
      return 'branded-parlay-picks';
    }
    return 'parlay-picks';
  }
  private client = sportslineGraphQLClient();

  private get getIsBrandedDataValid() {
    try {
      return [this.isBranded, Boolean(this.book), Boolean(this.bookName), Boolean(this.vendorBookId)].every(Boolean);
    } catch (error) {
      console.error('Error validating branded data:', error);
      return false;
    }
  }

  private get getBookName() {
    try {
      if (!(this.book && getBookAssetsById(`${this.book}`))) {
        return null;
      }
      return this.bookName || getBookAssetsById(`${this.book}`).name || 'Sportsbook';
    } catch (error) {
      console.error('Error getting book name:', error);
      return null;
    }
  }

  private get getWidgetDataInfo() {
    return {
      name: this.name,
      instanceId: this.instanceId,
      version: this.version,
      ...(this.getIsBrandedDataValid && {
        promotion: {
          operator: this.getBookName.toLowerCase(), 
          title: getBookSloganById(this.book),
        },
      }),
      ref: this.el,
    };
  }

  private trackError(error: Error) {
    this.error = `Error fetching data: ${error.message}`;
    console.log('Error fetching data:', this.error);
    console.warn('Error fetching data:', error);
    trackEvent({
      action: 'error',
      eventName: eventsName.martech,
      payload: {
        error: this.error,
      },
      widgetData: this.getWidgetDataInfo,
    });
  }

  private async fetch(input: PickSnippetInput[]) {
    this.initialized = true; // Mark as initialized to allow rendering
    this.loading = true;
    this.error = null;

    // fetching data, before loading the component
    if (!Boolean(input)) {
      console.warn('No input provided for SportslinePickSnippet');
      this.loading = false;
      this.error = 'No input provided for SportslinePickSnippet';
      return Promise.resolve();
    }
    return this.client.dataFetcher
      .SportslineWebComponent_PickSnippetParlay({
        input,
      })
      .then(response => {
        const data = response.data;
        this.data = data?.pickSnippetParlay;

        if (Boolean(response.errors?.length)) {
          throw new Error(`GraphQL errors: ${response.errors.map(error => error.message).join(', ')}`);
        }

        if (!Boolean(data?.pickSnippetParlay)) {
          throw new Error('No pick snippet parlay data found');
        }

        const pickAttributes = this.getDataFromAttribute;
        const picks = this.data.legs; // leg of the parlays

        const bets = pickAttributes.map((dataAttr, index) =>
          fromPickSnippetAttributeToTrackingViewedWidgetDataBets({
            dataAttr,
            pick: picks[index],
            position: index + 1,
          }),
        );
        const event: PickSnippetEventViewed = {
          action: 'viewed',
          eventName: eventsName.martech,
          widgetData: {
            ...this.getWidgetDataInfo,
            bets,
          },
        };
        trackEvent(event);
      });
  }

  async componentWillLoad() {
     // check if we have data-instance-id attribute, if so, use it for the instanceId,
    // if not generate one and set it as attribute
    this.instanceId = setInstanceIdAttribute(this.el, this.instanceId);
    try {
      const input = this.getInput;
      if (this.getDataPickAttribute === null) {
        throw new Error('No data-pick attribute found on SportslinePickSnippetParlay component');
      }
      // Track the initial impression event
      trackEvent({
        action: 'impression',
        eventName: eventsName.martech,
        widgetData: {
          ...this.getWidgetDataInfo,
          bets: this.getDataFromAttribute.map((dataAttr, index) =>
            fromPickSnippetAttributeToTrackingImpressionWidgetDataBets({
              dataAttr,
              position: index + 1,
            }),
          ),
        },
      });
      observeVisibilityAndTrigger({
        element: this.el,
        callback: async () => {
          await this.fetch(input)
            .catch((error: Error) => {
              // Track error if fetching fails
              this.trackError(error);
            })
            .finally(() => {
              this.loading = false;
              this.initialized = true; // Mark as initialized after fetching
            });
        },
      });
    } catch (error) {
      this.trackError(error);
      this.loading = false;
      this.initialized = true; // Mark as initialized after fetching
    }
  }

  private get getVendorData() {
    const validations = [!Boolean(this.data?.legs?.length), !(this.data?.legs && this.data.legs.every(l => isGameAvailableForBetting(l.game)))];
    if (validations.some(Boolean)) {
      return null;
    }
    return JSON.stringify(this.getSidelinesData);
  }

  private get renderVendorButton() {
    const className = 'cbs-widget';
    const validations = [this.el.getElementsByClassName(className).length > 0, !Boolean(this.getVendorData)];
    if (validations.some(Boolean)) {
      return undefined;
    }
    const el = document.createElement('div');
    el.classList.add(className);

    el.setAttribute('data-config', JSON.stringify(this.getSidelinesData));
    // sidelines.innerHTML = `BET NOW`;
    this.el.appendChild(el);
  }

  private get renderVendorDisclaimer() {
    const className = 'cbs-widget-disclaimer';
    const validations = [
      this.el.getElementsByClassName(className).length > 0,
      !(this.data?.legs && this.data.legs.length > 0),
      !(this.data?.legs && this.data.legs.every(l => isGameAvailableForBetting(l.game))),
    ];
    if (validations.some(Boolean)) {
      return;
    }
    const el = document.createElement('div');
    el.setAttribute('slot', 'disclaimer');
    el.innerHTML = `Betting odds are subject to change. Please gamble responsibly.`;
    el.classList.add(className);
    this.el.appendChild(el);
  }

  private get getInput() {
    const dataPick = this.getDataFromAttribute;
    if (dataPick.length > 0) return dataPick.map(d => toPickSnippetInput(d));
    return null;
  }

  // Remove typing and place correct one
  private get getDataFromAttribute() {
    const attributeData = JSON.parse(this.getDataPickAttribute);
    if (isUndefinedOrNull(attributeData) || !Array.isArray(attributeData)) {
      console.warn('Invalid or missing data-pick attribute on SportslinePickSnippetParlay component');
      return [];
    }
    return attributeData.map(d => getDataFromAttribute(JSON.stringify(d)));
  }

  private get getDataPickAttribute() {
    return this.el.getAttribute('data-pick') ?? null;
  }

  private get getLegs() {
    const attributeData = JSON.parse(this.getDataPickAttribute);
    if (isUndefinedOrNull(attributeData) || !Array.isArray(attributeData)) {
      console.warn('Invalid or missing data-pick attribute on SportslinePickSnippetParlay component');
      return [];
    }

    if (!this.data.legs || this.data.legs.length === 0) {
      console.warn('Legs data is missing in the response from the server');
      return [];
    }

    return this.data.legs.map((data, index) => {
      return fromPickSnippetToPickSnippetCardProps({
        data,
        dataAttr: JSON.stringify(attributeData[index]),
      });
    });
  }

  private get getSidelinesData() {
    const type = 'parlay';
    const brandedContent = this.getIsBrandedDataValid
      ? {
          type: type + '-branded',
          meta: {
            promotion: {
              operator: this.getBookName,
              title: getBookSloganById(this.book),
            }
         },
          operator: this.vendorBookId,
          operatorName: this.getBookName,
          vendorName: 'SRADAR', // Hardcoded
        }
      : {};

    return {
      type,
      instanceId: this.instanceId,
      ...brandedContent,
      markets: this.getLegs
        .map(l => {
          if (l.sideLinesData && typeof l.sideLinesData === 'object') {
            const { type, ...rest } = l.sideLinesData;
            return rest;
          } else {
            console.warn('Invalid or missing sideLinesData for leg:', l);
            return null; // Return null for any leg with invalid sideLinesData
          }
        })
        .filter(l => l !== null),
    };
  }

  render() {
    if (!this.initialized) return null; // Prevent rendering before initialization
    if (this.loading) {
      return (
        <Host>
          <div class="sportsline-pick-snippet-parlay--loading">
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }
    if (Boolean(this.error) || !Boolean(this.data)) {
      return null;
    }

    const isBranded = this.getIsBrandedDataValid;

    return (
      <Host>
        <sportsline-pick-snippet-parlay-card
          isBranded={isBranded}
          bookId={this.book}
          bookName={this.getBookName}
          bookSlogan={getBookSloganById(this.book)}
          odds={this.data.odds}
          wager={10}
          legs={this.getLegs}
        >
          {/* Not Pretty but works */}
          <slot>{this.renderVendorButton}</slot>
          {this.renderVendorDisclaimer}
          {isBranded && this.getVendorData && <sg-disclaimer slot="disclaimer" data-config={this.getVendorData}></sg-disclaimer>}
        </sportsline-pick-snippet-parlay-card>
        {/* <pre>{JSON.stringify(this.getSidelinesData, null, 2)}</pre> */}
      </Host>
    );
  }
}
